import type {
  InspectionInfo,
  AdminClientDetail,
  AdminFaqResponse,
  AdminFeedbacksResponse,
  FaqPublico,
  FaqQuestion,
  Feedback,
  MuralResponse,
  ResumoFeedbacks,
  SessaoAberta,
  AdminClientsResponse,
  ColorImportPreview,
  FeaturedAdminResponse,
  KitsResponse,
  KitSimulado,
  CotacaoSacola,
  Cupom,
  CuponsResponse,
  KitsAdminResponse,
  KindIconsAdminResponse,
  MarketplaceLinkResponse,
  AdminOrderDetail,
  AdminOrdersResponse,
  OrderEvent,
  InventoryResult,
  Order,
  OrderDetail,
  TradeInQuote,
  UpgradeQuote,
  FeaturedResponse,
  ColorsResponse,
  CredentialsApplied,
  CredentialsPreview,
  CredentialsStatus,
  MeResponse,
  PricingPreviewResponse,
  PricingTiersResponse,
  PresencaResponse,
  PublicUser,
  RulesResponse,
  TierInput,
  SyncProgress,
  FxHistoryResponse,
  FxRate,
  HealthResponse,
  ListingSort,
  ListingsResponse,
  PriceHistoryResponse,
  SkinSort,
  SkinsResponse,
  VariantsResponse,
  WatchlistResponse,
  WeaponsResponse,
  AuditoriaItem,
  MembroEquipe,
  Papel,
} from './types';
import { idsDoGa } from '../lib/tracking/gaIds';

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type QueryValue = string | number | boolean | undefined | null;

function buildUrl(path: string, params?: Record<string, QueryValue>): string {
  const url = `${BASE}/api${path}`;
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(path: string, params?: Record<string, QueryValue>, init?: RequestInit): Promise<T> {
  const res = await fetch(buildUrl(path, params), {
    // A sessão vive num cookie httpOnly: sem isto, toda chamada é anônima.
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });

  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }

  if (!res.ok) {
    const err = (body as { error?: { message?: string; code?: string; details?: unknown } } | null)?.error;
    // Perfil Desenvolvedor tentando alterar algo: avisa a tela inteira, porque
    // nem toda aba do painel mantém a mensagem de erro visível.
    if (err?.code === 'somente_leitura') window.dispatchEvent(new CustomEvent('dms:somente-leitura'));
    throw new ApiError(
      err?.message ?? `Falha na requisição (HTTP ${res.status})`,
      res.status,
      err?.code ?? 'http_error',
      err?.details,
    );
  }

  return body as T;
}

export const api = {
  health: () => request<HealthResponse>('/health'),

  fx: (source?: string, target?: string) => request<FxRate>('/fx', { source, target }),

  fxHistory: (days = 30) => request<FxHistoryResponse>('/fx/history', { days }),

  weapons: () => request<WeaponsResponse>('/weapons'),

  skins: (params: {
    type?: string;
    category?: string;
    search?: string;
    sort?: SkinSort;
    page?: number;
    pageSize?: number;
    stattrak?: 'only' | 'exclude' | 'all';
    kind?: string;
    /** StatTrak™: all | yes | no */
    st?: string;
    /** Lado do agente, separado por vírgula: "ct,tr" */
    team?: string;
    /** Cores separadas por vírgula (azul,rosa…). */
    color?: string;
    /** Faixa em reais (a API converte para yuan). */
    priceCurrency?: string;
    /** Listas separadas por vírgula: "FN,MW" */
    exterior?: string;
    rarity?: string;
    quality?: string;
    minPrice?: number;
    maxPrice?: number;
    force?: boolean;
  }) => request<SkinsResponse>('/skins', params),

  variants: (scope: { type?: string; category?: string }, key: string) =>
    request<VariantsResponse>('/skins/variants', { ...scope, key }),

  listings: (params: {
    templateId: number;
    sort?: ListingSort;
    limit?: number;
    page?: number;
    pageSize?: number;
    type?: string;
    /** Códigos de estilo separados por vírgula (p1,ruby,fade_90…). */
    styles?: string;
    /** Faixa de float e pattern exato. */
    floatMin?: string;
    floatMax?: string;
    seed?: string;
    /** false = lê só a página pedida, sem varrer a skin inteira. */
    scan?: boolean;
    force?: boolean;
  }) => request<ListingsResponse>('/listings', params),

  /** Fotos de inspeção da peça: os dois lados, com adesivos e desgaste reais. */
  inspection: (commodityId: string) =>
    request<InspectionInfo>(`/listings/${encodeURIComponent(commodityId)}/inspection`),

  history: (templateId: number, days = 30) =>
    request<PriceHistoryResponse>(`/templates/${templateId}/history`, { days }),

  harvestCatalog: (rounds = 6) =>
    request<{ rounds: number; seen: number; newItems: number; catalogSize: number }>('/catalog/harvest', undefined, {
      method: 'POST',
      body: JSON.stringify({ rounds }),
    }),

  syncStatus: () => request<SyncProgress>('/catalog/sync'),

  startSync: () => request<SyncProgress>('/catalog/sync', undefined, { method: 'POST', body: '{}' }),

  cancelSync: () => request<SyncProgress>('/catalog/sync/cancel', undefined, { method: 'POST' }),

  watchlist: () => request<WatchlistResponse>('/watchlist'),

  addToWatchlist: (input: { templateId: number; label?: string; targetPrice?: number; note?: string }) =>
    request<{ ok: boolean }>('/watchlist', undefined, { method: 'POST', body: JSON.stringify(input) }),

  removeFromWatchlist: (templateId: number) =>
    request<{ ok: boolean }>(`/watchlist/${templateId}`, undefined, { method: 'DELETE' }),

  // ── Conta ────────────────────────────────────────────────────────────────
  me: () => request<MeResponse>('/auth/me'),

  rules: () => request<RulesResponse>('/auth/rules'),

  logout: () => request<{ ok: boolean }>('/auth/logout', undefined, { method: 'POST' }),

  onboarding: (input: { tradeUrl: string; whatsapp?: string; acceptRules: true }) =>
    request<{ user: PublicUser }>('/account/onboarding', undefined, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  acceptRules: () => request<{ user: PublicUser }>('/account/accept-rules', undefined, { method: 'POST' }),

  // ── Painéis admin ────────────────────────────────────────────────────────
  adminTiers: () => request<PricingTiersResponse>('/admin/pricing/tiers'),

  adminSaveTiers: (tiers: TierInput[], feePct?: number, kitRateBonus?: number) =>
    request<{ tiers: PricingTiersResponse['tiers']; feePct: number }>('/admin/pricing/tiers', undefined, {
      method: 'PUT',
      body: JSON.stringify({ tiers, feePct, kitRateBonus }),
    }),

  adminPricingPreview: (params: { type?: string; kind?: string; limit?: number }) =>
    request<PricingPreviewResponse>('/admin/pricing/preview', params),

  adminClients: (params: { search?: string; status?: 'active' | 'blocked' | 'all'; flagged?: boolean; limit?: number }) =>
    request<AdminClientsResponse>('/admin/clients', params),

  adminClient: (id: number) => request<AdminClientDetail>(`/admin/clients/${id}`),

  adminSetClientStatus: (id: number, status: 'active' | 'blocked', reason?: string) =>
    request<{ client: AdminClientsResponse['items'][number] }>(`/admin/clients/${id}/status`, undefined, {
      method: 'POST',
      body: JSON.stringify({ status, reason }),
    }),

  featured: () => request<FeaturedResponse>('/featured'),

  // ── Pedidos ─────────────────────────────────────────────────────────────
  criarPedido: (input: {
    kind: 'encomenda' | 'venda' | 'upgrade';
    /** listingId identifica o anúncio; qty vale só no upgrade, por template. */
    items?: { templateId: number; listingId?: string; qty?: number }[];
    assetIds?: string[];
    note?: string;
    cupom?: string;
  }) =>
    request<{ order: Order; whatsapp: string | null }>('/orders', undefined, {
      method: 'POST',
      // Os identificadores do GA4 seguem com o pedido para a venda concluída,
      // que o servidor envia quando a equipe finaliza no painel.
      body: JSON.stringify({ ...input, ga: idsDoGa() ?? undefined }),
    }),

  meusPedidos: () => request<{ items: Order[] }>('/orders'),

  cotarSacola: (items: { templateId: number; listingId?: string }[], cupom?: string) =>
    request<CotacaoSacola>('/orders/cotacao', undefined, {
      method: 'POST',
      body: JSON.stringify({ items, cupom: cupom || undefined }),
    }),

  pedido: (code: string) => request<OrderDetail>(`/orders/${encodeURIComponent(code)}`),

  enviarPedido: (code: string) =>
    request<{ order: Order; whatsapp: string | null }>(`/orders/${encodeURIComponent(code)}/enviar`, undefined, {
      method: 'POST',
    }),

  cancelarPedido: (code: string, motivo?: string) =>
    request<{ order: Order }>(`/orders/${encodeURIComponent(code)}/cancelar`, undefined, {
      method: 'POST',
      body: JSON.stringify({ motivo }),
    }),

  adminPedidos: (params: { status?: string; kind?: string; search?: string; dias?: number; limit?: number }) =>
    request<AdminOrdersResponse>('/admin/orders', params),

  adminPedido: (code: string) => request<AdminOrderDetail>(`/admin/orders/${encodeURIComponent(code)}`),

  adminMudarStatusPedido: (code: string, status: string, opts: { motivo?: string; nota?: string } = {}) =>
    request<{ order: Order; eventos: OrderEvent[] }>(`/admin/orders/${encodeURIComponent(code)}/status`, undefined, {
      method: 'POST',
      body: JSON.stringify({ status, ...opts }),
    }),

  adminRemoverItemDoPedido: (code: string, itemId: number) =>
    request<{ order: Order; eventos: OrderEvent[] }>(`/admin/orders/${code}/itens/${itemId}`, undefined, {
      method: 'DELETE',
    }),

  adminAlterarItemDoPedido: (code: string, itemId: number, patch: { unitBrl?: number; qty?: number }) =>
    request<{ order: Order; eventos: OrderEvent[] }>(`/admin/orders/${code}/itens/${itemId}`, undefined, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  adminAdicionarItemAoPedido: (code: string, item: { templateId: number; listingId?: string; qty?: number }) =>
    request<{ order: Order; eventos: OrderEvent[] }>(`/admin/orders/${code}/itens`, undefined, {
      method: 'POST',
      body: JSON.stringify(item),
    }),

  adminNotaPedido: (code: string, nota: string) =>
    request<{ eventos: OrderEvent[] }>(`/admin/orders/${encodeURIComponent(code)}/nota`, undefined, {
      method: 'POST',
      body: JSON.stringify({ nota }),
    }),

  adminSalvarWhatsapp: (numero: string) =>
    request<{ whatsapp: string }>('/admin/orders/whatsapp', undefined, {
      method: 'PUT',
      body: JSON.stringify({ numero }),
    }),

  // ── Dúvidas ─────────────────────────────────────────────────────────────
  faq: () => request<FaqPublico>('/faq'),

  abrirPergunta: (id: number) =>
    request<SessaoAberta>(`/faq/${id}/abrir`, undefined, { method: 'POST' }),

  fecharSessao: (sessionId: number, outcome: 'resolvida' | 'nao_resolvida' | 'atendimento') =>
    request<{ whatsapp: string | null }>(`/faq/sessoes/${sessionId}/fechar`, undefined, {
      method: 'POST',
      body: JSON.stringify({ outcome }),
    }),

  linkAtendimento: () => request<{ whatsapp: string | null }>('/faq/atendimento'),

  adminMarketplaceLink: () => request<MarketplaceLinkResponse>('/admin/marketplace/link'),

  adminSalvarMarketplaceLink: (token: string) =>
    request<MarketplaceLinkResponse>('/admin/marketplace/link', undefined, {
      method: 'PUT',
      body: JSON.stringify({ token }),
    }),

  adminFaq: () => request<AdminFaqResponse>('/admin/faq'),

  adminCriarPergunta: (input: { question: string; answer: string }) =>
    request<{ question: FaqQuestion }>('/admin/faq', undefined, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  adminSalvarPergunta: (id: number, input: { question?: string; answer?: string; active?: boolean }) =>
    request<{ question: FaqQuestion }>(`/admin/faq/${id}`, undefined, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  adminApagarPergunta: (id: number) =>
    request<{ ok: boolean }>(`/admin/faq/${id}`, undefined, { method: 'DELETE' }),

  adminReordenarFaq: (ids: number[]) =>
    request<AdminFaqResponse>('/admin/faq/ordem/todas', undefined, {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),

  // ── Feedbacks ───────────────────────────────────────────────────────────
  mural: (limit?: number) => request<MuralResponse>('/feedbacks', { limit }),

  resumoFeedbacks: () => request<{ summary: ResumoFeedbacks }>('/feedbacks/resumo'),

  meuFeedback: () => request<{ feedback: Feedback | null }>('/feedbacks/meu'),

  salvarFeedback: (input: { rating: number; comment?: string }) =>
    request<{ feedback: Feedback }>('/feedbacks/meu', undefined, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  apagarFeedback: () => request<{ ok: boolean }>('/feedbacks/meu', undefined, { method: 'DELETE' }),

  adminFeedbacks: (params: { status?: string; limit?: number }) =>
    request<AdminFeedbacksResponse>('/admin/feedbacks', params),

  adminModerarFeedback: (id: number, status: string, motivo?: string) =>
    request<{ feedback: Feedback }>(`/admin/feedbacks/${id}/moderar`, undefined, {
      method: 'POST',
      body: JSON.stringify({ status, motivo }),
    }),

  adminResponderFeedback: (id: number, texto: string) =>
    request<{ feedback: Feedback }>(`/admin/feedbacks/${id}/resposta`, undefined, {
      method: 'PUT',
      body: JSON.stringify({ texto }),
    }),

  // ── Inventário, venda e upgrade ──────────────────────────────────────────
  inventory: (force = false) => request<InventoryResult>('/inventory', force ? { force: 1 } : undefined),

  tradeInQuote: (assetIds: string[]) =>
    request<TradeInQuote>('/trade-in/quote', undefined, { method: 'POST', body: JSON.stringify({ assetIds }) }),

  upgradeQuote: (assetIds: string[], wanted: { templateId: number; listingId?: string; qty: number }[]) =>
    request<UpgradeQuote>('/upgrade/quote', undefined, {
      method: 'POST',
      body: JSON.stringify({ assetIds, wanted }),
    }),

  adminFeatured: () => request<FeaturedAdminResponse>('/admin/featured'),

  adminAddFeatured: (input: { baseKey: string; name: string; weaponCode?: string | null; kind?: string | null }) =>
    request<{ items: FeaturedAdminResponse['items'] }>('/admin/featured', undefined, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  adminRemoveFeatured: (baseKey: string) =>
    request<{ items: FeaturedAdminResponse['items'] }>(`/admin/featured/${encodeURIComponent(baseKey)}`, undefined, {
      method: 'DELETE',
    }),

  adminReorderFeatured: (baseKeys: string[]) =>
    request<{ items: FeaturedAdminResponse['items'] }>('/admin/featured/order', undefined, {
      method: 'PUT',
      body: JSON.stringify({ baseKeys }),
    }),

  kits: () => request<KitsResponse>('/kits'),

  simularKit: (p: { knife: string; knifeExterior?: string; gloves: string; glovesExterior?: string }) =>
    request<KitSimulado>('/kits/simular', p),

  adminKits: () => request<KitsAdminResponse>('/admin/kits'),

  adminCreateKit: (input: {
    name: string;
    knifeBaseKey: string;
    knifeName: string;
    knifeExterior?: string | null;
    glovesBaseKey: string;
    glovesName: string;
    glovesExterior?: string | null;
    note?: string | null;
    colors?: string[];
  }) =>
    request<{ items: KitsAdminResponse['items'] }>('/admin/kits', undefined, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  adminUpdateKit: (
    id: number,
    patch: { name?: string; note?: string | null; active?: boolean; colors?: string[] },
  ) =>
    request<{ items: KitsAdminResponse['items'] }>(`/admin/kits/${id}`, undefined, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  adminRemoveKit: (id: number) =>
    request<{ items: KitsAdminResponse['items'] }>(`/admin/kits/${id}`, undefined, { method: 'DELETE' }),

  adminReorderKits: (ids: number[]) =>
    request<{ items: KitsAdminResponse['items'] }>('/admin/kits/order', undefined, {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),

  adminKindIcons: (kind?: string) => request<KindIconsAdminResponse>('/admin/kind-icons', { kind }),

  adminSetKindIcon: (kind: string, url: string | null) =>
    request<KindIconsAdminResponse>(`/admin/kind-icons/${encodeURIComponent(kind)}`, undefined, {
      method: 'PUT',
      body: JSON.stringify({ url }),
    }),

  adminColors: () => request<ColorsResponse>('/admin/colors'),

  adminPreviewColors: (fileBase64: string) =>
    request<ColorImportPreview>('/admin/colors/preview', undefined, {
      method: 'POST',
      body: JSON.stringify({ fileBase64 }),
    }),

  adminImportColors: (fileBase64: string) =>
    request<{ salvas: number; ignoradas: number; total: number }>('/admin/colors/import', undefined, {
      method: 'POST',
      body: JSON.stringify({ fileBase64 }),
    }),

  adminSetColor: (skinName: string, color: string | null) =>
    request<{ total: number }>('/admin/colors/set', undefined, {
      method: 'POST',
      body: JSON.stringify({ skinName, color }),
    }),

  adminCredentials: () => request<CredentialsStatus>('/admin/credentials/status'),
  adminOnline: () => request<PresencaResponse>('/admin/online'),

  adminCupons: () => request<CuponsResponse>('/admin/cupons'),

  adminCriarCupom: (input: Record<string, unknown>) =>
    request<{ items: Cupom[] }>('/admin/cupons', undefined, { method: 'POST', body: JSON.stringify(input) }),

  adminAtualizarCupom: (id: number, patch: Record<string, unknown>) =>
    request<{ items: Cupom[] }>(`/admin/cupons/${id}`, undefined, { method: 'PUT', body: JSON.stringify(patch) }),

  adminRemoverCupom: (id: number) =>
    request<{ items: Cupom[] }>(`/admin/cupons/${id}`, undefined, { method: 'DELETE' }),

  adminEquipe: () => request<{ eu: { id: number; papel: Papel } | null; membros: MembroEquipe[] }>('/admin/equipe'),

  adminCandidatosEquipe: (q: string) => request<{ items: MembroEquipe[] }>('/admin/equipe/candidatos', { q }),

  adminMudarPerfil: (id: number, papel: Exclude<Papel, 'master'>) =>
    request<{ membro: MembroEquipe }>(`/admin/equipe/${id}`, undefined, {
      method: 'PUT',
      body: JSON.stringify({ papel }),
    }),

  adminAuditoria: (p: { usuario?: number; busca?: string; antes?: number; limite?: number }) =>
    request<{ items: AuditoriaItem[]; proximo: number | null }>('/admin/auditoria', p),

  adminPreviewCredentials: (pasted: string) =>
    request<CredentialsPreview>('/admin/credentials/preview', undefined, {
      method: 'POST',
      body: JSON.stringify({ pasted }),
    }),

  adminApplyCredentials: (input: { pasted?: string; fields?: Record<string, string> }) =>
    request<CredentialsApplied>('/admin/credentials/apply', undefined, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  adminAckNameChange: (id: number) =>
    request<{ client: AdminClientsResponse['items'][number] }>(`/admin/clients/${id}/ack-name-change`, undefined, {
      method: 'POST',
    }),
};
